import React from 'react';
import { Formik, yupToFormErrors } from 'formik';
import { apiPost } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import CtaPrimary from 'components/ui/CtaPrimary';
import Form from 'components/ui/Form';
import * as Yup from 'yup';
import { getTranslatedString } from 'helpers/i18n';
import BorderlessCard from 'components/ui/BorderlessCard';
import moment from 'moment';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import { toFriendlyDateString } from 'helpers/Formatter';

const CONTACT_OPTIONS = [
  { id: 'phone', name: getTranslatedString('prospects.phone') },
  { id: 'email', name: getTranslatedString('prospects.email') },
  { id: 'sms', name: getTranslatedString('prospects.text') },
  { id: 'in_person', name: getTranslatedString('prospects.inPerson') },
];

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const Card = styled(BorderlessCard)`
  border-radius: 16px;
  max-height: 100%;

  .ant-card-body {
    padding: 0;
  }
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const DividerTitle = styled.p`
  margin: 0px;
  background-color: #f4f4f4;
  text-align: center;
  padding: 6px;
  font-weight: 800;
`;

const StyledCTAPrimary = styled(CtaPrimary)`
  margin-top: 8px;
  border-radius: 5px !important;
`;

export default class AddProspectNote extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: undefined,
    };
  }

  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');

    let validationSchema = {
      notes: Yup.string().required(requiredErrorVerbiage),
    };

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  validateContactForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');

    let validationSchema = {
      notes: Yup.string().required(requiredErrorVerbiage),
      contact_type: Yup.string().required(requiredErrorVerbiage),
      contact_date: Yup.date().required(requiredErrorVerbiage),
    };

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  handleFormSubmit = (values) => {
    const payload = {
      text: values.notes,
      first_name: this.props.prospect.first_name,
      last_name: this.props.prospect.last_name,
      contact_type: values.contact_type || 'general',
      contact_date:
        values.contact_date && toFriendlyDateString(values.contact_date, { language: 'en' }),
      lead_card_id: this.props.prospect.id,
    };
    this.props.onLoadingChange({ isLoading: true, values: payload });
    apiPost(
      Auth.getApiUrl() + `/landlords/leads/${this.props.prospect.lead_id}/tenant_notes/`,
      payload
    )
      .done((response) => {
        this.props.onSubmissionComplete(payload);
      })
      .catch((error) => {
        this.props.onLoadingChange({ isError: true, isLoading: false, values: payload });
      });
  };

  render() {
    return (
      <React.Fragment>
        <Card>
          <div>
            <DividerTitle>Contact log</DividerTitle>
          </div>
          <div style={{ padding: '24px 16px' }}>
            <p>{getTranslatedString('prospects.contactDetails')}</p>
            <Formik
              initialValues={{
                notes: undefined,
                contact_type: undefined,
                contact_date: undefined,
              }}
              onSubmit={(values) => this.handleFormSubmit(values)}
              validate={this.validateContactForm}
            >
              {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
                <VerticalFormWrapper id={'LAST_CONTACTED_FORM'} onSubmit={handleSubmit}>
                  {this.renderCreateNewProspectContact(
                    touched,
                    errors,
                    values,
                    handleChange,
                    setFieldValue
                  )}
                  <StyledCTAPrimary type="submit">
                    {getTranslatedString('prospects.addRecord')}
                  </StyledCTAPrimary>
                </VerticalFormWrapper>
              )}
            </Formik>
          </div>

          <div>
            <DividerTitle>Add a note</DividerTitle>
          </div>
          <div style={{ padding: '3px 16px 24px' }}>
            <Formik
              initialValues={{
                notes: undefined,
              }}
              onSubmit={(values) => this.handleFormSubmit(values)}
              validate={this.validateForm}
            >
              {({ values, errors, touched, handleChange, handleSubmit }) => (
                <VerticalFormWrapper id={this.props.formName} onSubmit={handleSubmit}>
                  {this.renderCreateNewProspectNotes(touched, errors, values, handleChange)}
                  <StyledCTAPrimary type="submit">
                    {getTranslatedString('prospects.addNote')}
                  </StyledCTAPrimary>
                </VerticalFormWrapper>
              )}
            </Formik>
          </div>
        </Card>
      </React.Fragment>
    );
  }

  renderCreateNewProspectNotes = (touched, errors, values, handleChange) => (
    <Spacer mt={0.5} mb={0.5}>
      <Form.Item
        validateStatus={touched.notes && errors.notes ? 'error' : null}
        help={touched.notes && errors.notes}
      >
        <p style={{ margin: 0 }}>Notes</p>
        <FullWidthTextArea
          placeholder={getTranslatedString('showingDrawer.addInternalNotes')}
          size="large"
          name="notes"
          value={values.notes}
          onChange={handleChange}
        />
      </Form.Item>
    </Spacer>
  );

  renderCreateNewProspectContact = (touched, errors, values, handleChange, setFieldValue) => (
    <React.Fragment>
      <Spacer mb={1}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('prospects.contactDate')}*`}
              validateStatus={touched.contact_date && errors.contact_date ? 'error' : null}
              help={touched.contact_date && errors.contact_date}
            >
              <FullWidthDatePicker
                name="contact_date"
                defaultValue={values.contact_date}
                value={values.contact_date}
                onChange={(value) => setFieldValue('contact_date', value, true)}
                disabledDate={(current) => current && current > moment().endOf('day')}
                placeholder="e.g. 2020-01-01"
                minDate={new Date()}
                mobileMode="date"
                size="large"
                id="error"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={`${getTranslatedString('prospects.contactType')}*`}
              validateStatus={touched.contact_type && errors.contact_type ? 'error' : null}
              help={touched.contact_type && errors.contact_type}
            >
              <FullWidthSelect
                name="contact_type"
                value={values.contact_type}
                onChange={(value) => setFieldValue('contact_type', value, true)}
                placeholder={getTranslatedString('showingDrawer.select')}
                size="large"
              >
                {CONTACT_OPTIONS.map((option) => (
                  <Select.Option value={option.id}>{option.name}</Select.Option>
                ))}
              </FullWidthSelect>
            </Form.Item>
          </Col>
        </Row>
      </Spacer>
      <Spacer mt={0.5} mb={0.5}>
        <Form.Item
          validateStatus={touched.notes && errors.notes ? 'error' : null}
          help={touched.notes && errors.notes}
        >
          <p style={{ margin: 0 }}>Notes</p>
          <FullWidthTextArea
            placeholder={getTranslatedString('showingDrawer.addInternalNotes')}
            size="large"
            name="notes"
            value={values.notes}
            onChange={handleChange}
          />
        </Form.Item>
      </Spacer>
    </React.Fragment>
  );
}
